import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/shared/Sidebar';
import Header from '../../components/shared/Header';
import DealMetricCard from '../../components/deals/DealMetricCard';
import DealFilters from '../../components/deals/DealFilters';
import DealTable from '../../components/deals/DealTable';
import { apiService, Deal } from '../../services/api';
import './Deals.css';

const Deals: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedDeals, setSelectedDeals] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [searchQuery, setSearchQuery] = useState('');
  const [dealData, setDealData] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch deals from API
  useEffect(() => {
    const abortController = new AbortController();

    const fetchDeals = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getAllDeals(abortController.signal);
        
        // Handle different response structures
        let dealsArray: any[] = [];
        if (response.deals && Array.isArray(response.deals)) {
          dealsArray = response.deals;
        } else if (response.data && Array.isArray(response.data)) {
          dealsArray = response.data;
        } else if (Array.isArray(response)) {
          dealsArray = response;
        }

        if (dealsArray.length > 0) {
          // Map API deals to frontend deal format
          const mappedDeals = dealsArray.map(apiDeal => 
            apiService.mapApiDealToDeal(apiDeal)
          );
          setDealData(mappedDeals);
        } else {
          setError('No deals found');
        }
      } catch (err: any) {
        // Ignore aborted requests
        if (err.name === 'AbortError') return;
        
        setError(err.message || 'An error occurred while fetching deals');
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();

    // Cleanup: cancel request if component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  const dealsPerPage = 8;

  // Filter deals based on search, status, and type
  const filteredDeals = dealData.filter(deal => {
    const matchesSearch = 
      deal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      deal.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      deal.discountLabel.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'All Status' || 
      deal.status === statusFilter.toLowerCase();

    const matchesType = typeFilter === 'All Types' || 
      deal.type.toLowerCase() === typeFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesType;
  });

  // Calculate metrics from filtered deals
  const totalDeals = filteredDeals.length;
  const activeDeals = filteredDeals.filter(d => d.status === 'active').length;
  const totalUsage = filteredDeals.reduce((sum, deal) => sum + deal.usage, 0);
  const avgDiscount = filteredDeals.length > 0
    ? filteredDeals.reduce((sum, deal) => {
        const discount = parseFloat(deal.discountLabel.replace(/[^0-9.]/g, '')) || 0;
        return sum + discount;
      }, 0) / filteredDeals.length
    : 0;

  // Paginate filtered deals
  const totalPages = Math.ceil(totalDeals / dealsPerPage);
  const startIndex = (currentPage - 1) * dealsPerPage;
  const endIndex = startIndex + dealsPerPage;
  const paginatedDeals = filteredDeals.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, typeFilter]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSelectDeal = (dealId: string) => {
    setSelectedDeals((prev) =>
      prev.includes(dealId) ? prev.filter((id) => id !== dealId) : [...prev, dealId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDeals.length === paginatedDeals.length) {
      setSelectedDeals([]);
    } else {
      setSelectedDeals(paginatedDeals.map((deal) => deal.id));
    }
  };

  const handleToggleActive = (dealId: string) => {
    setDealData((prev) =>
      prev.map((deal) =>
        deal.id === dealId ? { ...deal, isActive: !deal.isActive } : deal
      )
    );
  };

  return (
    <div className="dashboard-container">
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      <div className={`dashboard-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header page="deals" />
        <main className="dashboard-content">
          <div className="deals-header-section">
            <div>
              <h1 className="deals-title">Deals</h1>
              <p className="deals-subtitle">Manage promotional deals and discounts.</p>
            </div>
            <button className="deals-create-btn">+ Create Deal</button>
          </div>

          <div className="deals-metrics">
            <DealMetricCard title="Active Deals" value={activeDeals.toLocaleString()} icon="🏷️" />
            <DealMetricCard title="Total Usage" value={totalUsage.toLocaleString()} icon="📈" />
            <DealMetricCard
              title="Revenue Impact"
              value="$0"
              icon="📊"
              accent="green"
            />
            <DealMetricCard title="Avg. Discount" value={`${avgDiscount.toFixed(1)}%`} icon="💡" />
          </div>

          <DealFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
          />

          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Loading deals...</p>
            </div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
              <p>Error: {error}</p>
            </div>
          ) : (
            <DealTable
              deals={paginatedDeals}
              selectedDeals={selectedDeals}
              onSelectDeal={handleSelectDeal}
              onSelectAll={handleSelectAll}
              onToggleActive={handleToggleActive}
            />
          )}

          {!loading && !error && totalDeals > 0 && (
            <div className="deals-pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * dealsPerPage) + 1} to{' '}
                {Math.min(currentPage * dealsPerPage, totalDeals)} of {totalDeals} deals
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  className="pagination-btn"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {!loading && !error && totalDeals === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>No deals found.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Deals;

